import React, { useMemo, useState } from "react";
import { Button, Slider } from "../../../ui";
import { formatMonthLong } from "../../usage/utils/months";
import type { Granularity } from "../types";
import {
  computeStops,
  indexForValue,
  latestStopValue,
  periodForValue,
  periodOptions,
  valueForStop,
} from "../utils/timeSlider";

interface TimeSliderProps {
  granularity: Granularity;
  /** Earliest data for the domain (bounds the period selector); null = no history. */
  availableFrom: string | null;
  /** Current `as_of` (null = the live "now" view). */
  value: string | null;
  onChange: (asOf: string | null) => void;
}

/**
 * History scrubber for the failure-mode map (#1261): a period selector — a
 * month (daily) / a year (weekly), spanning all history back to
 * `availableFrom` — plus a slider over that period's calendar days / ISO
 * weeks. The current period's right-most stop is the live "now" view.
 * Picking a period commits its most recent stop immediately; the slider only
 * refetches on release (Radix `onValueCommit`), so a drag is a single
 * request — while dragging, only the local label follows the thumb.
 */
export function TimeSlider({
  granularity,
  availableFrom,
  value,
  onChange,
}: TimeSliderProps) {
  const periods = useMemo(
    () => (availableFrom ? periodOptions(granularity, availableFrom) : []),
    [granularity, availableFrom],
  );
  const period = availableFrom
    ? periodForValue(granularity, value, availableFrom)
    : null;
  const stops = useMemo(
    () =>
      availableFrom && period
        ? computeStops(granularity, period, availableFrom)
        : [],
    [granularity, period, availableFrom],
  );
  // While dragging, track the thumb locally so the label follows the cursor;
  // the fetch (onChange) only fires on release. Cleared in the commit, period
  // and jump-to-now handlers; clamped below so a period switch can never
  // leave it out of range.
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  if (!availableFrom || period === null || stops.length === 0) return null;
  // A single period whose only stop is the live edge — under one day/week of
  // history, nothing to scrub.
  if (periods.length === 1 && stops.length < 2) return null;

  const committedIndex = indexForValue(stops, value);
  const index = Math.min(dragIndex ?? committedIndex, stops.length - 1);
  const stop = stops[index];

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
      <span className="whitespace-nowrap text-xs font-medium text-muted-foreground">
        Viewing
      </span>
      <select
        value={period}
        onChange={(e) => {
          setDragIndex(null);
          onChange(latestStopValue(granularity, e.target.value, availableFrom));
        }}
        className="flex h-8 rounded-md border border-input bg-card px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={granularity === "daily" ? "Select month" : "Select year"}
      >
        {periods.map((p) => (
          <option key={p} value={p}>
            {granularity === "daily" ? formatMonthLong(p) : p}
          </option>
        ))}
      </select>
      <Slider
        className="flex-1"
        min={0}
        max={stops.length - 1}
        step={1}
        value={[index]}
        disabled={stops.length < 2}
        onValueChange={(v) => setDragIndex(v[0])}
        onValueCommit={(v) => {
          setDragIndex(null);
          onChange(valueForStop(stops[v[0]]));
        }}
        aria-label="Scrub failure clusters through time"
      />
      <span className="w-40 whitespace-nowrap text-right text-xs text-foreground">
        {stop.label}
      </span>
      {!stop.isLive && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setDragIndex(null);
            onChange(null);
          }}
        >
          Jump to now
        </Button>
      )}
    </div>
  );
}
